import React, { Component } from 'react';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';

import { Modal, Button, Select, Checkbox, Divider, message, Row, Col } from 'antd';
import { API_BASE_URL } from '../../config/api';

class AcknowledgePanelModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      physicianCode: null,
      agreed: false,
      saving: false,
      error: '',
    };
  }

  handleSign = async () => {
    const { physicianCode, agreed } = this.state;
    const { physicians = [] } = this.props;
    const physician = physicians.find(p => p.PhysicianCode === physicianCode);
    if (!physician) {
      this.setState({ error: 'Please select the physician signing this acknowledgement.' });
      return;
    }
    if (!agreed) {
      this.setState({ error: 'Please confirm you have read and understand the requirements above.' });
      return;
    }

    this.setState({ saving: true, error: '' });
    const { client, panel } = this.props;
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      this.setState({ saving: false });
      return;
    }

    axios({
      method: 'POST',
      url: `${API_BASE_URL}/connect/panels/${client.ClientCode}/${panel.PanelCode}/acknowledge`,
      // year: sign THIS row's request — without it the backend defaults to the
      // current year and a prior-year item would never actually get signed.
      data: { acknowledgedBy: physician.FullName, year: panel.year },
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      message.success('Panel acknowledged');
      this.props.onSigned(panel, res.data.acknowledgement || {
        acknowledgedBy: physician.FullName,
        acknowledgedAt: new Date().toISOString(),
      });
    }).catch(err => {
      console.log(err);
      message.error('Failed to submit acknowledgement');
      this.setState({ saving: false });
    });
  }

  // Two-column list of the panel's component tests — the list is usually what
  // makes this modal tall, so splitting it is the main space win.
  renderTestsList(tests, columns = 2) {
    return (
      // "160px N" = at most N columns, but fewer when the container is narrow —
      // this is what collapses the list to a single column on phones.
      <ul style={{ columns: `160px ${columns}`, columnGap: 28, margin: '4px 0 0', paddingLeft: 18, fontSize: 12 }}>
        {(tests || []).map((t, i) => (
          <li key={i} style={{ breakInside: 'avoid', marginBottom: 2 }}>
            {t.ComponentTestName || t.ComponentTestCode}
          </li>
        ))}
      </ul>
    );
  }

  renderComponentsCard(columns) {
    const { panel } = this.props;
    return (
      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 14px' }}>
        <p style={{ margin: 0, fontSize: 12 }}>
          The custom profile you have requested includes the following components which when
          medically necessary, will be reimbursed as follows:
        </p>
        <p style={{ fontWeight: 600, margin: '8px 0 0' }}>Profile Name: {panel.PanelName}</p>
        {this.renderTestsList(panel.tests, columns)}
        <p style={{ fontSize: 9, color: "#888", margin: "8px 0 0" }}>
          *The Medicaid reimbursement amount will be equal to or less than the Medicare reimbursement amount.
        </p>
      </div>
    );
  }

  renderReadStep() {
    const { client, panel } = this.props;
    const clientAddress = [client.Address, client.City, client.State, client.Zip].filter(Boolean).join(', ');

    // Small panels: components card sits beside the letter in 2 columns.
    // Large ones (most panels are ≤10 components; a few reach ~58) would make
    // that side card tower over everything — go full-width below the letter
    // with 4 columns instead.
    const largePanel = (panel.tests || []).length > 24;

    return (
      <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 12, flexWrap: 'wrap', gap: 4 }}>
          <div>
            <div><strong>{client.ClientName}</strong> (#{client.ClientCode})</div>
            <div style={{ color: '#666' }}>{clientAddress}</div>
          </div>
          <div>{moment().format('MMMM D, YYYY')}</div>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <Row gutter={[24, 12]}>
          <Col xs={24} md={largePanel ? 24 : 13}>
            <p style={{ marginTop: 0, fontSize: 12 }}>
              In allowing the use of custom profiles, we are required to obtain annually, your signature
              below, acknowledging you have read and understood all the requirements surrounding custom
              profiles. In the event we do not receive this signed acknowledgment, we will not be able to
              recognize your custom profile listed below. Should you have any questions, please contact
              our Laboratory at 908-583-5421.
            </p>

            <p style={{ marginBottom: 4, fontSize: 12 }}>This acknowledgment is also to remind you that:</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              <li>
                When ordering tests for which Medicare reimbursement will be sought, you, as the physician,
                should only order those tests which you believe are medically necessary for each patient.
              </li>
              <li>
                Utilizing a custom profile may result in the ordering of tests for which Medicare or other
                federally funded payers may deny payment.
              </li>
              <li>
                Only those tests medically necessary in treating a patient's condition should be ordered.
                Therefore, when all of the components of a custom profile are not medically necessary, you
                should order only those individual tests or a less inclusive profile which are medically
                necessary for treating a patient.
              </li>
              <li>
                The Office of Inspector General (OIG) takes the position that a physician who orders
                medically unnecessary tests for which Medicare reimbursement is claimed may be subject to
                civil penalties.
              </li>
            </ul>
            <p style={{ marginBottom: 0, fontSize: 12 }}>
              Our facility has a clinical consultant available to assist you in ensuring appropriate tests
              are ordered.
            </p>
          </Col>

          {!largePanel && (
            <Col xs={24} md={11}>
              {this.renderComponentsCard(2)}
            </Col>
          )}
        </Row>

        {largePanel && (
          <div style={{ marginTop: 12 }}>
            {this.renderComponentsCard(4)}
          </div>
        )}
      </div>
    );
  }

  // Signature controls live at the bottom of the same page as the letter —
  // one screen, no steps.
  renderSignBlock() {
    const { physicians = [] } = this.props;
    const { physicianCode, agreed, error } = this.state;

    return (
      <div style={{ fontSize: 12 }}>
        <Divider style={{ margin: '10px 0' }} />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <span style={{ fontWeight: 600, marginRight: 8 }}>Physician Name</span>
            <Select
              size="small"
              value={physicianCode}
              onChange={(v) => this.setState({ physicianCode: v })}
              placeholder="Select physician to sign"
              style={{ width: 'min(320px, calc(100vw - 120px))' }}
              popupClassName="ack-modal-dropdown"
              options={physicians.map(p => ({ value: p.PhysicianCode, label: p.FullName }))}
            />
          </div>
          <Checkbox
            checked={agreed}
            onChange={(e) => this.setState({ agreed: e.target.checked })}
          >
            I have read and understand the requirements above
          </Checkbox>
        </div>
        <p style={{ fontSize: 12, color: "#888", margin: "4px 0 0" }}>
          Selecting your name above and submitting acts as your signature. Date: {moment().format('MMMM D, YYYY')}
        </p>
        {error && (
          <p style={{ color: 'red', margin: '6px 0 0' }}>{error}</p>
        )}
      </div>
    );
  }

  renderSignedView() {
    const { panel } = this.props;
    return (
      <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        <p style={{ fontWeight: 600, marginTop: 0 }}>Profile Name: {panel.PanelName}</p>
        {this.renderTestsList(panel.tests, (panel.tests || []).length > 24 ? 4 : 2)}
        <Divider style={{ margin: '10px 0' }} />
        <p style={{ marginBottom: 2 }}>Physician Name: {panel.acknowledgedBy}</p>
        <p style={{ marginBottom: 2 }}>
          Signed on: {panel.acknowledgedAt ? moment(panel.acknowledgedAt).format('MMMM D, YYYY') : ''}
        </p>
      </div>
    );
  }

  render() {
    const { onClose } = this.props;
    const alreadySigned = !!this.props.panel.acknowledged;
    const { saving } = this.state;

    let footer;
    let body;
    if (alreadySigned) {
      body = this.renderSignedView();
      footer = <Button onClick={onClose}>Close</Button>;
    } else {
      body = (
        <>
          {this.renderReadStep()}
          {this.renderSignBlock()}
        </>
      );
      footer = (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={this.handleSign}>
            Acknowledge & Sign
          </Button>
        </>
      );
    }

    return (
      <Modal
        className="ack-modal"
        title="Custom Panel Acknowledgement"
        open
        onCancel={onClose}
        width={Math.min(1150, window.innerWidth - 32)}
        style={{ top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', overflowX: 'hidden' } }}
        footer={footer}
      >
        {/* antd components don't inherit inline font sizes — scope the small
            type to this modal (and the Select's dropdown, which portals
            outside the modal DOM). */}
        <style>{`
          .ack-modal .ant-modal-title { font-size: 13px; }
          .ack-modal .ant-checkbox-wrapper { font-size: 10px; }
          .ack-modal .ant-select { font-size: 10px; }
          .ack-modal .ant-select-selection-item,
          .ack-modal .ant-select-selection-placeholder { font-size: 10px; }
          .ack-modal .ant-btn { font-size: 12px; }
          .ack-modal-dropdown .ant-select-item { font-size: 10px; min-height: 24px; }
        `}</style>
        {body}
      </Modal>
    );
  }
}

export default AcknowledgePanelModal;
